import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useSendDocumentsDialog } from "./useSendDocumentsDialog";
import SendOverridesSection from "./SendOverridesSection";

const clientOptionLabel = (option) =>
  `${option.name}${option.email ? ` · ${option.email}` : ""}${
    option.passportNumber ? ` · ${option.passportNumber}` : ""
  }`;

/** Send one or more of this college's mapped documents to a client, optionally linked to one of
 * that client's open admissions. */
const SendCollegeDocumentsDialog = ({
  open,
  universityId,
  folderId,
  preselectedFileId,
  onClose,
  onSent,
}) => {
  const {
    mappedFiles,
    selectedFileIds,
    toggleFile,
    clientOptions,
    client,
    setClient,
    setClientQuery,
    admissionOptions,
    admissionsLoading,
    admissionsError,
    admissionId,
    setAdmissionId,
    message,
    setMessage,
    pendingOverrideFields,
    overridesByFile,
    setOverrideValue,
    sending,
    canSubmit,
    handleSend,
  } = useSendDocumentsDialog({ open, universityId, folderId, preselectedFileId, onClose, onSent });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Send documents to a client</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack>
            <Typography variant="subtitle2">Documents</Typography>
            {mappedFiles.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No mapped documents in this folder yet.
              </Typography>
            )}
            {mappedFiles.map((file) => (
              <FormControlLabel
                key={file.id}
                control={
                  <Checkbox
                    checked={selectedFileIds.includes(file.id)}
                    onChange={() => toggleFile(file.id)}
                  />
                }
                label={`${file.name} (${file.mappedFieldCount} fields)`}
              />
            ))}
          </Stack>

          <Autocomplete
            options={clientOptions}
            getOptionLabel={clientOptionLabel}
            filterOptions={(options) => options}
            value={client}
            onChange={(_, value) => setClient(value)}
            onInputChange={(_, value) => setClientQuery(value)}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Client"
                placeholder="Search name, email or passport number"
              />
            )}
          />

          {client && admissionsLoading && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Checking this client&apos;s admissions...
              </Typography>
            </Stack>
          )}

          {client && admissionsError && (
            <Alert severity="error">
              Could not check this client&apos;s admissions ({admissionsError}). You can still send,
              the signed document just will not be automatically linked to one.
            </Alert>
          )}

          {client && !admissionsLoading && !admissionsError && admissionOptions.length === 0 && (
            <Alert severity="warning">
              This client has no open admissions. You can still send; the signed copy just will not
              be filed against an admission.
            </Alert>
          )}

          {client && !admissionsError && admissionOptions.length > 0 && (
            <TextField
              select
              fullWidth
              label="Link to admission (optional)"
              value={admissionId}
              onChange={(event) => setAdmissionId(event.target.value)}
              helperText="The signed copy is filed in this admission's Documents tab. Clear it for documents that aren't about an admission."
            >
              <MenuItem value="">Not linked to an admission</MenuItem>
              {admissionOptions.map((admission) => (
                <MenuItem key={admission.id} value={String(admission.id)}>
                  {admission.name || `Admission ${admission.id}`}
                  {admission.intake ? ` · ${admission.intake}` : ""}
                  {admission.status ? ` · ${admission.status}` : ""}
                </MenuItem>
              ))}
            </TextField>
          )}

          <SendOverridesSection
            fields={pendingOverrideFields}
            overridesByFile={overridesByFile}
            onChange={setOverrideValue}
          />

          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Message to the client (optional)"
            value={message}
            onChange={(event) => setMessage(event.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!canSubmit || sending} onClick={handleSend}>
          Send {selectedFileIds.length > 0 ? `(${selectedFileIds.length})` : ""}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendCollegeDocumentsDialog;
